// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AskPunttInvitePicker.test.tsx
import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { Plus } from "@phosphor-icons/react";
import {
  Avatar,
  Badge,
  Flex,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  buildClassifiedSearchResults,
  buildNewEmailCandidate,
  shouldOfferNewEmailInvite,
  type InvitePickerMode,
  type InviteTier,
} from "../../utils/askPunttInviteCandidate";
import { useSearchEnterpriseUsers } from "../../utils/queries/askPuntt";

export interface AskPunttInvitePickerProps {
  open: boolean;
  mode: InvitePickerMode;
  searchInputId: string;
  searchTestId: string;
  resultsTestId: string;
  loadingTestId: string;
  emptyTestId: string;
  resultRowTestIdPrefix: string;
  addButtonTestIdPrefix: string;
  emailInviteTestId?: string;
  /** Users who already have access to the project/chat. */
  accessMembers: AskPunttChatUserSummary[];
  /** Project roster for tier-1 tagging (defaults to accessMembers in project mode). */
  projectMembers?: AskPunttChatUserSummary[];
  isPending: boolean;
  onInviteUser: (user: AskPunttChatUserSummary, tier: InviteTier) => void;
  onInviteEmail: (email: string) => void;
}

function tierBadgeColor(
  tier: InviteTier,
): "blue" | "gray" | "green" | undefined {
  if (tier === "project") return "blue";
  if (tier === "enterprise") return "gray";
  return "green";
}

export function AskPunttInvitePicker({
  open,
  mode,
  searchInputId,
  searchTestId,
  resultsTestId,
  loadingTestId,
  emptyTestId,
  resultRowTestIdPrefix,
  addButtonTestIdPrefix,
  emailInviteTestId = "ask-puntt-invite-email-row",
  accessMembers,
  projectMembers,
  isPending,
  onInviteUser,
  onInviteEmail,
}: AskPunttInvitePickerProps) {
  const { t } = useTranslation("ui");
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 250);
    return () => window.clearTimeout(handle);
  }, [rawQuery]);

  useEffect(() => {
    if (open) {
      setRawQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  const trimmedQuery = debouncedQuery.trim();
  const search = useSearchEnterpriseUsers(trimmedQuery, {
    enabled: open && trimmedQuery.length >= 1,
  });

  const accessUserIds = useMemo(
    () => new Set(accessMembers.map((m) => m._id)),
    [accessMembers],
  );

  const projectMemberIds = useMemo(() => {
    const roster = projectMembers ?? (mode === "project" ? accessMembers : []);
    return new Set(roster.map((m) => m._id));
  }, [accessMembers, mode, projectMembers]);

  const classifiedResults = useMemo(
    () =>
      buildClassifiedSearchResults(search.data?.users ?? [], {
        mode,
        accessUserIds,
        projectMemberIds,
      }),
    [accessUserIds, mode, projectMemberIds, search.data?.users],
  );

  const newEmailCandidate = useMemo(() => {
    if (!shouldOfferNewEmailInvite(trimmedQuery, search.data?.users ?? [])) {
      return null;
    }
    return buildNewEmailCandidate(trimmedQuery);
  }, [search.data?.users, trimmedQuery]);

  const showEmptyState =
    trimmedQuery.length >= 1 &&
    !search.isLoading &&
    classifiedResults.length === 0 &&
    !newEmailCandidate;

  return (
    <>
      <Text as="label" size="2" weight="medium" htmlFor={searchInputId}>
        {t("invitePicker.searchLabel")}
      </Text>
      <TextField.Root
        id={searchInputId}
        data-testid={searchTestId}
        placeholder={t("invitePicker.searchPlaceholder")}
        value={rawQuery}
        onChange={(e) => setRawQuery(e.target.value)}
        mt="1"
        autoComplete="off"
      />

      {trimmedQuery.length >= 1 ? (
        <Flex
          direction="column"
          gap="1"
          mt="2"
          data-testid={resultsTestId}
          role="list"
          aria-label={t("invitePicker.searchResultsLabel")}
        >
          {search.isLoading ? (
            <Text size="2" color="gray" data-testid={loadingTestId}>
              {t("invitePicker.searching")}
            </Text>
          ) : null}

          {classifiedResults.map(
            ({ user, tier, tagLabel, canInvite, disabledReason }) => (
              <Flex
                key={user._id}
                align="center"
                gap="2"
                data-testid={`${resultRowTestIdPrefix}${user._id}`}
                role="listitem"
                className="rounded-md px-2 py-1.5 hover:bg-puntt-neutral-gray-3"
              >
                <Avatar
                  src={user.avatar ?? undefined}
                  fallback={(user.name || user.email || "?")
                    .charAt(0)
                    .toUpperCase()}
                  size="1"
                  radius="full"
                />
                <Flex direction="column" className="min-w-0 flex-1">
                  <Flex align="center" gap="2" wrap="wrap">
                    <Text size="2" weight="medium" className="truncate">
                      {user.name?.trim() || user.email?.trim() || user._id}
                    </Text>
                    <Badge size="1" color={tierBadgeColor(tier)} variant="soft">
                      {tagLabel}
                    </Badge>
                  </Flex>
                  {user.name && user.email ? (
                    <Text size="1" color="gray" className="truncate">
                      {user.email}
                    </Text>
                  ) : null}
                  {!canInvite && disabledReason ? (
                    <Text size="1" color="gray">
                      {disabledReason}
                    </Text>
                  ) : null}
                </Flex>
                <IconButton
                  data-testid={`${addButtonTestIdPrefix}${user._id}`}
                  aria-label={t("invitePicker.inviteUserAriaLabel", {
                    name:
                      user.name ||
                      user.email ||
                      t("invitePicker.fallbackUserName"),
                  })}
                  variant="soft"
                  size="1"
                  disabled={isPending || !canInvite}
                  onClick={() => onInviteUser(user, tier)}
                >
                  <Plus size={14} aria-hidden="true" />
                </IconButton>
              </Flex>
            ),
          )}

          {newEmailCandidate ? (
            <Flex
              align="center"
              gap="2"
              data-testid={emailInviteTestId}
              role="listitem"
              className="rounded-md px-2 py-1.5 hover:bg-puntt-neutral-gray-3"
            >
              <Flex direction="column" className="min-w-0 flex-1">
                <Flex align="center" gap="2" wrap="wrap">
                  <Text size="2" weight="medium" className="truncate">
                    {newEmailCandidate.email}
                  </Text>
                  <Badge size="1" color="green" variant="soft">
                    {newEmailCandidate.tagLabel}
                  </Badge>
                </Flex>
                <Text size="1" color="gray">
                  {t("invitePicker.sendInvitationDescription")}
                </Text>
              </Flex>
              <IconButton
                data-testid={`${addButtonTestIdPrefix}email`}
                aria-label={t("invitePicker.inviteEmailAriaLabel", {
                  email: newEmailCandidate.email,
                })}
                variant="soft"
                size="1"
                disabled={isPending}
                onClick={() => onInviteEmail(newEmailCandidate.email)}
              >
                <Plus size={14} aria-hidden="true" />
              </IconButton>
            </Flex>
          ) : null}

          {showEmptyState ? (
            <Text size="2" color="gray" data-testid={emptyTestId}>
              {t("invitePicker.noMatches", { query: trimmedQuery })}
            </Text>
          ) : null}
        </Flex>
      ) : null}
    </>
  );
}
